import React, { useState, useEffect, useMemo } from 'react';
import { get, post } from '../utils/api';
import { Container, Form, Button, Modal, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { getRoles } from '../utils/roles';
import { formatPermissionName } from '../utils/permissionUtils';
import { 
  FaUserShield, FaCrown, FaUserMd, FaTruck, FaUser, 
  FaCheckCircle, FaLock, FaCog, FaShieldAlt, FaCapsules, 
  FaShoppingCart, FaUsersCog, FaSave, FaTimes, FaCheckSquare, FaSquare
} from 'react-icons/fa';
import { useToast } from './ToastContext';
import './RoleManagement.css';

const getRoleMeta = (roleName) => {
  switch ((roleName || '').toLowerCase()) {
    case 'admin':
      return {
        icon: <FaCrown />,
        colorClass: 'role-admin',
        title: 'System Administrator',
        description: 'Full unconstrained privileges across all subsystems, user credentials, financials and system audit logs.',
        isImmutable: true,
        scope: 'Enterprise Root Access'
      };
    case 'pharmacist':
      return {
        icon: <FaUserMd />,
        colorClass: 'role-pharmacist',
        title: 'Licensed Pharmacist',
        description: 'Authorized to conduct counter POS sales, verify medical prescriptions, dispatch medicines and audit batch inventory.',
        isImmutable: false,
        scope: 'Clinical & Sales Desk'
      };
    case 'delivery':
      return {
        icon: <FaTruck />,
        colorClass: 'role-delivery',
        title: 'Delivery Personnel',
        description: 'Assigned to field order fulfillment, parcel transit tracking, delivery dispatches and counter delivery manifests.',
        isImmutable: false,
        scope: 'Logistics & Dispatch'
      };
    case 'customer':
      return {
        icon: <FaUser />,
        colorClass: 'role-customer',
        title: 'Retail Customer',
        description: 'Standard retail consumer access to browse active medicine catalog, upload prescriptions and track orders.',
        isImmutable: false,
        scope: 'Storefront & Patient Portal'
      };
    default:
      return {
        icon: <FaUserShield />,
        colorClass: 'role-default',
        title: roleName,
        description: 'Custom organizational role with granular permissions configured through the Role Matrix.',
        isImmutable: false,
        scope: 'Custom Access'
      };
  }
};

const RoleManagement = () => {
  const { showToast } = useToast();
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        getRoles(),
        get('/permissions')
      ]);

      if (rolesRes && rolesRes.success) {
        setRoles(rolesRes.data || []);
      }
      if (permsRes && permsRes.success) {
        setAllPermissions(permsRes.data || []);
      }
    } catch (err) {
      showToast('Failed to load roles or permissions matrix.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = async (role) => {
    setSelectedRole(role);
    try {
      const response = await get(`/permissions/role/${role.name}`);
      if (response && response.success) {
        const rolePermissionIds = (response.data || []).map(p => p.permission_id);
        setSelectedPermissions(rolePermissionIds);
      } else {
        setSelectedPermissions([]);
      }
    } catch (err) {
      setSelectedPermissions([]);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRole(null);
    setSelectedPermissions([]);
  };

  const handlePermissionChange = (permissionId) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId) 
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSelectGroup = (permissionIds, select = true) => {
    setSelectedPermissions(prev => {
      const set = new Set(prev);
      permissionIds.forEach(id => {
        if (select) set.add(id);
        else set.delete(id);
      });
      return Array.from(set);
    });
  };

  const handleAssignPermissions = async () => {
    if (!selectedRole) return;
    setSaving(true);

    try {
      const response = await post(`/permissions/role/${selectedRole.name}`, { 
        permission_ids: selectedPermissions 
      });
      if (response && response.success) {
        showToast(`Permissions updated for ${selectedRole.name} successfully!`, 'success');
        handleCloseModal();
      } else {
        showToast(response?.message || 'Failed to assign permissions.', 'danger');
      }
    } catch (err) {
      showToast(`Failed to assign permissions: ${err.message}`, 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Group permissions logically
  const groupedPermissions = useMemo(() => {
    const groups = {
      administration: {
        title: 'User & Security Administration',
        icon: <FaUsersCog className="text-primary me-2" />,
        items: []
      },
      inventory: {
        title: 'Inventory & Batch Supply Chain',
        icon: <FaCapsules className="text-info me-2" />,
        items: []
      },
      sales: {
        title: 'Point of Sale, Orders & Logistics',
        icon: <FaShoppingCart className="text-success me-2" />,
        items: []
      },
      other: {
        title: 'General System Capabilities',
        icon: <FaShieldAlt className="text-warning me-2" />,
        items: []
      }
    };

    allPermissions.forEach(p => {
      const name = p.permission_name.toLowerCase();
      if (name.includes('user') || name.includes('role') || name.includes('customer') || name.includes('person')) {
        groups.administration.items.push(p);
      } else if (name.includes('medicine') || name.includes('batch') || name.includes('supplier') || name.includes('purchase')) {
        groups.inventory.items.push(p);
      } else if (name.includes('order') || name.includes('prescription') || name.includes('payment') || name.includes('deliver')) {
        groups.sales.items.push(p);
      } else {
        groups.other.items.push(p);
      }
    });

    return Object.entries(groups).filter(([_, g]) => g.items.length > 0);
  }, [allPermissions]);

  return (
    <Container fluid className="role-mgmt-page px-3 px-lg-4 py-3">
      {/* Header & KPI Summary */}
      <div className="role-header-panel mb-4 p-3 p-lg-4 rounded glassmorphic-panel">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="role-master-icon">
              <FaUserShield />
            </div>
            <div>
              <h3 className="role-title mb-0">Role & Permission Matrix</h3>
              <p className="role-subtitle mb-0 text-muted">Configure access control levels, feature privileges and module capabilities across user roles.</p>
            </div>
          </div>

          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={fetchInitialData} 
            className="role-refresh-btn d-flex align-items-center gap-2"
          >
            <FaShieldAlt /> Refresh Matrix
          </Button>
        </div>

        {/* Roles KPI Summary Cards */}
        <Row className="g-3">
          <Col xs={6} md={3}>
            <div className="role-kpi-card">
              <div className="kpi-icon admin">
                <FaCrown />
              </div>
              <div className="kpi-content">
                <div className="kpi-value">Administrator</div>
                <div className="kpi-label">Full Privileges</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="role-kpi-card">
              <div className="kpi-icon pharmacist">
                <FaUserMd />
              </div>
              <div className="kpi-content">
                <div className="kpi-value">Pharmacist</div>
                <div className="kpi-label">POS & Rx Control</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="role-kpi-card">
              <div className="kpi-icon delivery">
                <FaTruck />
              </div>
              <div className="kpi-content">
                <div className="kpi-value">Delivery</div>
                <div className="kpi-label">Logistics Dispatch</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="role-kpi-card">
              <div className="kpi-icon customer">
                <FaUser />
              </div>
              <div className="kpi-content">
                <div className="kpi-value">Customer</div>
                <div className="kpi-label">Retail Portal</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Role Cards Grid */}
      <div className="role-cards-grid">
        {roles.map(role => {
          const meta = getRoleMeta(role.name);

          return (
            <div className={`role-card-item glassmorphic-panel ${meta.colorClass}`} key={role.role_id}>
              <div className="role-card-header p-4 d-flex justify-content-between align-items-start border-bottom">
                <div className="d-flex align-items-center gap-3">
                  <div className={`role-icon-box ${meta.colorClass}`}>
                    {meta.icon}
                  </div>
                  <div>
                    <h5 className="role-card-title mb-1">{meta.title}</h5>
                    <span className="role-scope-badge">{meta.scope}</span>
                  </div>
                </div>

                {meta.isImmutable ? (
                  <span className="badge bg-warning text-dark d-flex align-items-center gap-1">
                    <FaLock /> Root Lock
                  </span>
                ) : (
                  <span className="badge bg-primary">Configurable</span>
                )}
              </div>

              <div className="role-card-body p-4">
                <p className="role-description-text mb-3">
                  {meta.description}
                </p>

                <div className="role-stat-line p-3 rounded mb-2 d-flex justify-content-between align-items-center">
                  <span className="text-muted small">Access Level:</span>
                  <span className="text-white fw-bold small">
                    {meta.isImmutable ? 'Full Root Authority' : 'Granular Matrix Access'}
                  </span>
                </div>
              </div>

              <div className="role-card-footer p-3 border-top">
                {meta.isImmutable ? (
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    className="w-100 role-manage-btn disabled-btn" 
                    disabled
                  >
                    <FaLock className="me-2" /> Root System Privileges
                  </Button>
                ) : (
                  <Button 
                    variant="primary" 
                    size="sm" 
                    className="w-100 role-manage-btn"
                    onClick={() => handleShowModal(role)}
                  >
                    <FaCog className="me-2" /> Configure Permissions
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Permission Matrix Modal */}
      {selectedRole && (
        <Modal show={showModal} onHide={handleCloseModal} size="lg" scrollable className="role-modal-modern">
          <Modal.Header closeButton className="role-modal-header py-3 px-4">
            <div className="d-flex align-items-center gap-3">
              <div className="role-modal-icon">
                <FaShieldAlt />
              </div>
              <div>
                <Modal.Title className="role-modal-title mb-0">
                  Configure Permissions: <span className="text-primary text-capitalize">{selectedRole.name}</span>
                </Modal.Title>
                <div className="role-modal-subtitle">
                  Selected <span className="fw-bold text-info">{selectedPermissions.length}</span> of {allPermissions.length} capabilities
                </div>
              </div>
            </div>
          </Modal.Header>

          <Modal.Body className="role-modal-body p-4">
            <div className="permission-groups-wrapper">
              {groupedPermissions.map(([groupKey, group]) => {
                const groupItemIds = group.items.map(i => i.permission_id);
                const allSelected = groupItemIds.every(id => selectedPermissions.includes(id));
                const someSelected = groupItemIds.some(id => selectedPermissions.includes(id));

                return (
                  <div className="permission-group-card p-3 rounded mb-4" key={groupKey}>
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                      <div className="d-flex align-items-center">
                        {group.icon}
                        <h6 className="permission-group-title mb-0">{group.title}</h6>
                      </div>

                      <div className="d-flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline-info" 
                          className="permission-batch-btn"
                          onClick={() => handleSelectGroup(groupItemIds, !allSelected)}
                        >
                          {allSelected ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                    </div>

                    <Row className="g-2">
                      {group.items.map(perm => {
                        const isChecked = selectedPermissions.includes(perm.permission_id);

                        return (
                          <Col xs={12} md={6} key={perm.permission_id}>
                            <div 
                              className={`permission-item-box p-2 rounded d-flex align-items-center gap-2 ${isChecked ? 'selected' : ''}`}
                              onClick={() => handlePermissionChange(perm.permission_id)}
                            >
                              <Form.Check 
                                type="checkbox"
                                id={`perm-${perm.permission_id}`}
                                checked={isChecked}
                                onChange={() => {}}
                                className="permission-checkbox"
                              />
                              <label htmlFor={`perm-${perm.permission_id}`} className="permission-label mb-0 cursor-pointer flex-grow-1">
                                <span className="fw-semibold text-white">{formatPermissionName(perm.permission_name)}</span>
                              </label>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                );
              })}
            </div>
          </Modal.Body>

          <Modal.Footer className="role-modal-footer d-flex justify-content-between py-3 px-4">
            <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAssignPermissions} disabled={saving} className="role-submit-btn fw-bold px-4">
              {saving ? <><Spinner animation="border" size="sm" className="me-1" /> Saving Matrix...</> : <><FaSave className="me-2" /> Save Permissions</>}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
};

export default RoleManagement;
